export const IS_DEV = import.meta.env.DEV

export const QueryStateKeys = {
  TableSortBy: 'tableSortBy',
  TableSortType: 'tableSortType',
  TablePage: 'tablePage',
  TablePageSize: 'tablePageSize',
  GenerateLinkId: 'generateLinkId',
  HideIncompleteVerifications: 'hideIncompleteVerifications',
  HideIncompleteCases: 'HideIncompleteCases',
  SearchKey: 'searchKey',
  SearchValue: 'searchValue',
  ShowActiveUsers: 'ShowActiveUsers',
  onlyInactiveWorkflows: 'onlyInactiveWorkflows',
  workflowStatus: 'workflowStatus',
  WorkflowName: 'workflowName',
} as const

export const SessionStorageKeys = {
  resendEmailTimer: 'resendEmailTimer',
  googleAuthRedirect: 'googleAuthRedirect',
} as const

export const localStorageKeys = {
  accessToken: 'dtAccessToken',
  xUserClientToken: 'dtXUserClientToken',
  verificationId: (key: string) => `dtVerificationId-${key}`,
  userClientChatThread: (verificationId: string, formId: string) =>
    `dtUserClientChatThread-${verificationId}-${formId}`,
  legacyUserClientChatThread: (verificationId: string) => `dtUserClientChatThread-${verificationId}`,
  userClientFormChatThread: (verificationId: string, formId: string) =>
    `dtUserClientFormChatThread-${verificationId}-${formId}`,
  legacyUserClientFormChatThread: (verificationId: string) => `dtUserClientFormChatThread-${verificationId}`,
  // Floating vs docked panel layout, per chat kind (not per form/verification) — the layout is a
  // user preference that should carry across whatever they open next.
  chatDisplayMode: (chat: string) => `dtChatMode-${chat}`,
  // Dragged panel width, per chat kind and layout — a floating window and a docked column want
  // different widths, so they remember them separately.
  chatWidth: (chat: string, layout: string) => `dtChatWidth-${chat}-${layout}`,
  formBuilderChatThread: (key: string) => `dtFormBuilderChatThread-${key}`,
  // The onboarding assistant's current thread, per verified email and optional organisation
  // (see onboarding-chat).
  onboardingChatThread: (key: string) => `dtOnboardingChatThread-${key}`,
  // The verification form last opened from the onboarding assistant, per verified email (see
  // useOnboardingApplication).
  onboardingApplication: (email: string) => `dtOnboardingApplication-${email}`,
  // Answers typed into the form preview while testing a form (see preview-form-data-storage).
  formPreviewData: (formId: string) => `dtFormPreviewData-${formId}`,
  // Sandbox applicant that completed identity verification in the form preview — the preview has
  // no profile to store it on. `personKey` is set for a person added in the form (see preview-sumsub).
  formPreviewSumsubApplicant: (workflowId: string, personKey?: string) =>
    personKey
      ? `dtFormPreviewSumsubApplicant-${workflowId}-${personKey}`
      : `dtFormPreviewSumsubApplicant-${workflowId}`,
  // Legacy: the page assistant's per-path thread now lives in the database (see
  // use-page-chat-thread). Kept so a thread written by the localStorage-only build is offered
  // to the server once and adopted, rather than stranding that conversation.
  pageChatThread: (pagePath: string) => `dtPageChatThread-${pagePath}`,
  forms: 'dtForms',
  verification: 'dtVerification',
  lastActivity: 'dtLastActivity',
  logoutLock: 'dtLogoutLock',
  caseNotesFilters: 'dtCaseNotesFilters',
  caseDocumentsFilters: 'dtCaseDocumentsFilters',
} as const

export const TableParamsKey = {
  Links: 'links',
  Profiles: 'profiles',
  Verifications: 'verifications',
  Cases: 'cases',
  Workflows: 'workflows',
  ProfileVerifications: 'profile-verifications',
  ProfileCases: 'profile-cases',
  CaseProfiles: 'case-profiles',
  CaseVerifications: 'case-verifications',
  CaseDocuments: 'case-documents',
  CaseNotes: 'case-notes',
  CaseAudit: 'case-audit',
  TeamAudit: 'team-audit',
  TeamUsers: 'team-users',
  VerificationProfiles: 'verification-profiles',
  VerificationAudit: 'verification-audit',
  ApiKeyGenerate: 'api-key-generate',
  ApiKeyView: 'api-key-view',
  DashboardCases: 'dashboard-cases',
  UserSettingsProduct: 'user-settings-product',
  UserSettingsEntity: 'user-settings-entity',
  ScreeningsNaturalPersons: 'screenings-natural-persons',
  ScreeningsLegalEntity: 'screenings-legal-entity',
  Webhooks: 'webhooks',
  AgentRunRiskScoreCases: 'agent-run-risk-score-cases',
} as const

export type TableParamsKeyValue = (typeof TableParamsKey)[keyof typeof TableParamsKey]
